import React from 'react';
import {observer} from 'mobx-react';
import {t, withState} from 'shared/core';
import {types} from 'main/tools';
import {InteractiveAccordionTable, FancyFilter, DropdownFilter} from 'components';
import {Goal} from 'main/stores/performance_goals';
import {GoalListState} from '../state';
import useModal from 'shared/hooks/useModal';
import DuplicateGoalModal from 'main/containers/performance_goals/components/DuplicateGoalModal';
import {
  GoalNameCell,
  AssigneeCell,
  PeriodCell,
  StatusCell,
  ProgressCell,
  MetricNameCell
} from './GoalView';

const GOAL_COLUMNS = [
  {
    header: 'goals.Goal',
    sortBy: 'title',
    width: 4,
    responsive: {
      large: 5
    },
    component: GoalNameCell
  },
  {
    header: 'goals.Assignee',
    sortBy: 'assignee',
    width: 1,
    responsive: {
      large: 2
    },
    component: AssigneeCell
  },
  {
    header: 'goals.Period',
    sortBy: 'start_date',
    width: 3,
    responsive: {
      xlarge: 'hidden'
    },
    component: PeriodCell
  },
  {
    header: 'goals.Status',
    width: 1,
    responsive: {
      large: 'hidden',
      xlarge: 2
    },
    component: StatusCell
  },
  {
    header: 'goals.Progress',
    sortBy: 'progress',
    width: 2,
    responsive: {
      large: 5,
      xlarge: 4
    },
    component: ProgressCell
  }
];

const METRIC_COLUMNS = [
  {
    width: 9,
    responsive: {
      large: 6,
      xlarge: 7
    },
    component: MetricNameCell
  },
  {
    width: 2,
    responsive: {
      large: 5,
      xlarge: 4
    },
    component: ProgressCell
}
];

const GoalList = observer(({uiState, title, endpoint, showEmployeeFilters}) => {
  const {canCreateGoals} = uiState;
  const duplicateGoalModalOpen = useModal(uiState, 'DuplicateGoalModal', 'selectedGoal', Goal);

  const filters = ['goal_status', 'goal_employee', 'location', 'department', 'employment_type'];
  const statusOptions = [
    {
      id: 'all',
      status: 'all',
      display: t('goals.All')
    },
    {
      id: 'in_progress',
      status: 'in_progress',
      display: t('goals.status.In Progress')
    },
    {
      id: 'upcoming',
      status: 'upcoming',
      display: t('goals.status.Upcoming')
    },
    {
      id: 'past_due',
      status: 'past_due',
      display: t('goals.status.Past Due')
    },
    {
      id: 'complete',
      status: 'complete',
      display: t('goals.status.Complete')
    }
  ];

  const nestedRows = [
    {
      attribute: 'sortedMetrics',
      columns: METRIC_COLUMNS
    },
    {
      attribute: 'sortedSubgoals'
    }
  ];

  return (
    <React.Fragment>
      <InteractiveAccordionTable
        title={title}
        showAddButton={canCreateGoals}
        onAdd={() => uiState.goToNewGoalPage()}
        onMount={agent => uiState.setInteractiveAgent(agent)}
        onRefresh={args => uiState.updateInteractiveContextState(args)}
        saveStateToUrl
        searchable
        fancyFiltersEnabled={showEmployeeFilters}
        columns={GOAL_COLUMNS}
        canExpand={(goal) => uiState.canViewNestedGoals && goal.canViewFullDetails}
        onExpand={(goal) => uiState.fetchSubgoals(goal)}
        nestedRows={nestedRows}
        proxy={{
          type: 'async',
          endpoint: endpoint,
          modelType: types.PERFORMANCE_GOALS.GOAL,
          model: Goal
        }}
        uiState={uiState}
        editRoute='/edit/:id'
        onRemove={(goal) => uiState.deleteGoal(goal)}
        localization={{
          emptyStateMessage: 'goals.No goals to display',
          removeModal: {
            header: 'goals.Remove Goal?',
            subHeader: 'goals.Are you sure you want to delete this goal?',
            body: 'goals.This goal and all the associated comments and progress updates will be deleted forever.'
          },
          interactive: {
            addModel: 'goals.Create Goal'
          }
        }}
        customLinks={(model) => uiState.customLinksFor(model)}
      >
        {showEmployeeFilters ?
          <FancyFilter filters={filters}/> :
          <DropdownFilter options={statusOptions} attr='goal_status'/>}
      </InteractiveAccordionTable>
      <DuplicateGoalModal modalOpen={duplicateGoalModalOpen} uiState={uiState}/>
    </React.Fragment>
  );
});

export default withState(GoalList, GoalListState);
